/**
 * Filtro de Transportadora da Doca Expedição (#MT-B18B728).
 * A listagem da API só aceita igualdade. As outras condições são aplicadas na grade.
 */

const TEXT_OPS = new Set(['co', 'nc', 'bw', 'ew', 'nb', 'nd', 'si']);
const NULL_OPS = new Set(['nu', 'nn']);
const ID_OPS = new Set(['eq', 'ne', 'gt', 'lt', 'ge', 'le']);

/**
 * Chave do datalist: número puro ou `empresa:id`.
 * @param {string|number|null|undefined} raw
 * @returns {{ companyId: string|null, carrierId: string }|null}
 */
export function parseCarrierFilter(raw)
{
	const text = String(raw ?? '').trim();
	if (!text)
		return null;
	const colon = text.lastIndexOf(':');
	if (colon > 0 && /^\d+$/.test(text.slice(colon + 1)))
	{
		return {
			companyId: text.slice(0, colon),
			carrierId: text.slice(colon + 1),
		};
	}
	if (/^\d+$/.test(text))
		return { companyId: null, carrierId: text };
	return null;
}

/**
 * Linhas do filtro cuja coluna é a transportadora.
 * @param {object|null|undefined} filterData
 * @returns {{ op: string, raw: string }[]}
 */
export function carrierClausesFromFilter(filterData)
{
	const clauses = [];
	for (let i = 0; i < 12; i++)
	{
		if (filterData?.[`field-${i}`] !== 'carrier_id')
			continue;
		const op = String(filterData[`operator-${i}`] || 'eq').toLowerCase();
		const raw = filterData[`value-${i}`];
		if (!NULL_OPS.has(op) && (raw == null || String(raw).trim() === ''))
			continue;
		clauses.push({
			op,
			raw: raw == null ? '' : String(raw).trim(),
		});
	}
	return clauses;
}

/**
 * Igual a com código resolvido pode ir na API. Qualquer outra condição filtra na grade.
 * @param {{ op: string, raw: string }[]} clauses
 * @returns {{ companyId: string|null, carrierId: string }|null}
 */
export function carrierEqualityForApi(clauses)
{
	if (!Array.isArray(clauses) || clauses.length !== 1)
		return null;
	const clause = clauses[0];
	if (clause.op !== 'eq')
		return null;
	return parseCarrierFilter(clause.raw);
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function normCarrierText(value)
{
	return String(value ?? '')
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.replace(/\(cod\.\s*\d+\)/g, ' ')
		.replace(/[^a-z0-9]+/g, ' ')
		.trim()
		.replace(/\s+/g, ' ');
}

/**
 * @param {object} row
 * @returns {string}
 */
function carrierBlob(row)
{
	return normCarrierText([row?.carrier_nick, row?.carrier_name].filter(Boolean).join(' '));
}

/**
 * O valor da lista é o rótulo inteiro (apelido · nome · CNPJ).
 * Texto curto usa a condição no apelido/nome. Rótulo da lista também casa o cadastro.
 * @param {string} op
 * @param {string} blob
 * @param {string} needle
 * @returns {boolean}
 */
function textOpMatch(op, blob, needle)
{
	if (!blob || !needle)
		return op === 'nc' || op === 'nb' || op === 'nd' || op === 'ne';
	const inName = blob.includes(needle);
	const labelHasCarrier = blob.length >= 3 && needle.includes(blob);
	switch (op)
	{
		case 'eq':
		case 'co':
		case 'si':
			return inName || labelHasCarrier;
		case 'ne':
		case 'nc':
			return !(inName || labelHasCarrier);
		case 'bw':
			return blob.startsWith(needle) || labelHasCarrier;
		case 'ew':
			return blob.endsWith(needle) || labelHasCarrier;
		case 'nb':
			return !(blob.startsWith(needle) || labelHasCarrier);
		case 'nd':
			return !(blob.endsWith(needle) || labelHasCarrier);
		default:
			return false;
	}
}

/**
 * @param {object} row
 * @param {{ op: string, raw: string }} clause
 * @returns {boolean}
 */
export function dockRowMatchesCarrierClause(row, clause)
{
	const op = String(clause?.op || 'eq').toLowerCase();
	if (op === 'nu')
		return row?.carrier_id == null || row?.carrier_id === '';
	if (op === 'nn')
		return row?.carrier_id != null && row?.carrier_id !== '';

	const parsed = parseCarrierFilter(clause?.raw);
	if (parsed && ID_OPS.has(op))
	{
		if (parsed.companyId && String(row?.company_id) !== String(parsed.companyId))
			return op === 'ne';
		const id = row?.carrier_id == null || row?.carrier_id === '' ? null : Number(row.carrier_id);
		const target = Number(parsed.carrierId);
		if (op === 'eq')
			return id === target;
		if (op === 'ne')
			return id !== target;
		if (id == null || Number.isNaN(id))
			return false;
		if (op === 'gt')
			return id > target;
		if (op === 'lt')
			return id < target;
		if (op === 'ge')
			return id >= target;
		if (op === 'le')
			return id <= target;
		return false;
	}

	if (TEXT_OPS.has(op) || op === 'eq' || op === 'ne')
		return textOpMatch(op, carrierBlob(row), normCarrierText(clause?.raw));
	return false;
}

/**
 * @param {object[]} rows
 * @param {{ op: string, raw: string }[]} clauses
 * @returns {object[]}
 */
export function filterDockRowsByCarrier(rows, clauses)
{
	const list = Array.isArray(clauses) ? clauses : [];
	if (!list.length)
		return Array.isArray(rows) ? rows : [];
	return (Array.isArray(rows) ? rows : []).filter((row) =>
		list.every((clause) => dockRowMatchesCarrierClause(row, clause))
	);
}
